<script setup>
import { computed } from 'vue'

const PREVIEW_WIDTH = 640
const PREVIEW_HEIGHT = 320
const PREVIEW_PADDING = 38

const props = defineProps({
    farm: { type: Object, required: true },
})

const validCoordinate = coordinate => Array.isArray(coordinate)
    && coordinate.length >= 2
    && Number.isFinite(Number(coordinate[0]))
    && Number.isFinite(Number(coordinate[1]))
    && Number(coordinate[0]) >= -180
    && Number(coordinate[0]) <= 180
    && Number(coordinate[1]) >= -90
    && Number(coordinate[1]) <= 90

const coordinateFrom = (longitude, latitude) => {
    if (longitude === null || longitude === undefined || longitude === ''
        || latitude === null || latitude === undefined || latitude === '') {
        return null
    }

    const coordinate = [Number(longitude), Number(latitude)]
    return validCoordinate(coordinate) ? coordinate : null
}

const boundary = computed(() => (props.farm.area || [])
    .map(point => coordinateFrom(point.lon, point.lat))
    .filter(validCoordinate))

const farmCenter = computed(() => coordinateFrom(props.farm.lon, props.farm.lat))
const hasBoundary = computed(() => boundary.value.length >= 3)
const hasLocation = computed(() => hasBoundary.value || farmCenter.value !== null)

const projectedBoundary = computed(() => {
    if (!hasBoundary.value) {
        return ''
    }

    const longitudes = boundary.value.map(coordinate => coordinate[0])
    const latitudes = boundary.value.map(coordinate => coordinate[1])
    const minimumLongitude = Math.min(...longitudes)
    const maximumLongitude = Math.max(...longitudes)
    const minimumLatitude = Math.min(...latitudes)
    const maximumLatitude = Math.max(...latitudes)
    const longitudeSpan = Math.max(maximumLongitude - minimumLongitude, Number.EPSILON)
    const latitudeSpan = Math.max(maximumLatitude - minimumLatitude, Number.EPSILON)
    const availableWidth = PREVIEW_WIDTH - (PREVIEW_PADDING * 2)
    const availableHeight = PREVIEW_HEIGHT - (PREVIEW_PADDING * 2)
    const scale = Math.min(availableWidth / longitudeSpan, availableHeight / latitudeSpan)
    const renderedWidth = longitudeSpan * scale
    const renderedHeight = latitudeSpan * scale
    const horizontalOffset = (PREVIEW_WIDTH - renderedWidth) / 2
    const verticalOffset = (PREVIEW_HEIGHT - renderedHeight) / 2

    return boundary.value.map(coordinate => {
        const x = horizontalOffset + ((coordinate[0] - minimumLongitude) * scale)
        const y = verticalOffset + ((maximumLatitude - coordinate[1]) * scale)
        return `${x.toFixed(2)},${y.toFixed(2)}`
    }).join(' ')
})
</script>

<template>
    <div
        class="relative w-full overflow-hidden farm-map-preview"
        role="img"
        :aria-label="`Linderos registrados de la finca ${farm.name}`"
    >
        <svg
            v-if="hasLocation"
            class="absolute inset-0 w-full h-full"
            :viewBox="`0 0 ${PREVIEW_WIDTH} ${PREVIEW_HEIGHT}`"
            preserveAspectRatio="none"
            aria-hidden="true"
        >
            <rect width="100%" height="100%" fill="#eef4e8" />

            <polygon
                v-if="hasBoundary"
                :points="projectedBoundary"
                fill="#F3E022"
                fill-opacity="0.55"
                stroke="#03662E"
                stroke-width="5"
                stroke-linejoin="round"
                vector-effect="non-scaling-stroke"
            />

            <g v-else transform="translate(320 160)">
                <circle r="16" fill="#F3E022" stroke="#03662E" stroke-width="6" vector-effect="non-scaling-stroke" />
                <circle r="4" fill="#03662E" />
            </g>
        </svg>

        <div
            v-if="!hasLocation"
            class="absolute inset-0 flex flex-col items-center justify-center px-4 text-center farm-map-message text-enriqueto_green"
        >
            <span class="mb-1 text-2xl material-icons">location_on</span>
            <span class="text-sm font-semibold">Ubicación no disponible</span>
        </div>

        <div v-if="hasLocation" class="absolute z-10 flex items-center justify-center font-bold bg-white rounded-full shadow-sm pointer-events-none farm-map-north text-enriqueto_green">
            N
        </div>
    </div>
</template>

<style scoped>
.farm-map-preview {
    min-height: 10rem;
    aspect-ratio: 2 / 1;
    background: #dce8d4;
    border-radius: 1.5rem 1.5rem 0 0;
}

.farm-map-message {
    background-color: #eef4e8;
    background-image:
        linear-gradient(rgba(3, 102, 46, 0.08) 1px, transparent 1px),
        linear-gradient(90deg, rgba(3, 102, 46, 0.08) 1px, transparent 1px);
    background-size: 24px 24px;
}

.farm-map-north {
    right: 0.75rem;
    top: 0.75rem;
    width: 2rem;
    height: 2rem;
}
</style>
